import React from "react";
import Modal from "./Modal";
import { useAppLanguage } from "hooks/useAppLanguage";

export type ConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** Use 'danger' styling for destructive actions (default). */
  variant?: "danger" | "primary";
};

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "danger",
}: ConfirmDialogProps) {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    try {
      setBusy(true);
      await onConfirm();
      onClose();
    } catch {
      // Keep dialog open so the caller can show an inline error.
    } finally {
      setBusy(false);
    }
  };

  const confirmClass =
    variant === "danger"
      ? "bg-red-500 hover:bg-red-600 text-white"
      : "bg-brand-500 hover:bg-brand-600 text-white";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-60 dark:bg-white/10 dark:text-white"
          >
            {cancelLabel ?? (isArabic ? "إلغاء" : "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60 ${confirmClass}`}
          >
            {busy ? (isArabic ? "..." : "...") : confirmLabel ?? (isArabic ? "تأكيد" : "Confirm")}
          </button>
        </>
      }
    >
      {description ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
      ) : null}
    </Modal>
  );
}
